import {
  MicrophoneIcon,
  CameraIcon,
  UserIcon,
  ViewGridAddIcon,
  VideoCameraIcon,
  MusicNoteIcon,
} from '@heroicons/react/outline';
import { ChatAlt2Icon, PlayIcon } from '@heroicons/react/solid';
import Image from 'next/image';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import React, { useEffect, useRef, useState } from 'react';
import { FaImage, FaRobot } from 'react-icons/fa6';
import { GiSofa, GiSoundWaves } from 'react-icons/gi';

import clsxm from '@/lib/clsxm';
import { useSpecialOffer } from '@/utils/useSpecialOffer';
import { chatModels } from '@/constants/chatModels';

const FeatureCard = ({
  title,
  description,
  icon: Icon,
  imageSrc,
  imageAlt,
  href,
  className,
  imageClassName,
  videoSrc,
  customContent,
  isSpecialOffer,
  t,
  withSound = false,
  showControls = false,
}: {
  title: string;
  description: string | React.ReactNode;
  icon: any;
  imageSrc?: string;
  imageAlt?: string;
  href: string;
  className?: string;
  imageClassName?: string;
  videoSrc?: string;
  customContent?: React.ReactNode;
  isSpecialOffer: boolean;
  t: any;
  withSound?: boolean;
  showControls?: boolean;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  // Track if video source has been loaded (prevents iOS Safari from preloading)
  const [videoLoaded, setVideoLoaded] = useState(false);

  const togglePlay = () => {
    if (videoRef.current) {
      // Load video source on first play (iOS Safari ignores preload='none')
      if (!videoLoaded && videoSrc) {
        setVideoLoaded(true);
        // Need to wait for source to be added before playing
        setTimeout(() => {
          videoRef.current?.load();
          videoRef.current?.play().catch(() => {});
          setIsPlaying(true);
          setHasStarted(true);
        }, 0);
        return;
      }
      if (videoRef.current.paused) {
        videoRef.current.play().catch(() => {});
        setIsPlaying(true);
        setHasStarted(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  return (
    <div
      className={clsxm(
        'group relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-sm transition-all hover:bg-white/10 hover:shadow-2xl hover:shadow-purple-500/10',
        className,
      )}
      style={{
        // content-visibility: auto defers rendering of off-screen content for better performance
        contentVisibility: 'auto',
        containIntrinsicBlockSize: '500px',
      }}
    >
      <div className='flex flex-col h-full'>
        <div className='mb-6 flex items-center gap-4'>
          <span className='flex h-12 w-12 items-center justify-center rounded-xl bg-purple-500/20 text-purple-400 ring-1 ring-purple-500/30'>
            <Icon className='h-6 w-6' aria-hidden='true' />
          </span>
          <h3 className='text-2xl font-bold text-white'>{title}</h3>
        </div>

        <div className='mb-8 flex-grow'>
          <p className='text-lg leading-relaxed text-slate-300'>
            {description}
          </p>
          {!isSpecialOffer && (
            <Link
              href={href}
              className='mt-4 inline-flex items-center text-sm font-semibold text-purple-400 hover:text-purple-300'
            >
              {t('Learn more')} <span className='ml-1'>→</span>
            </Link>
          )}
        </div>

        <div className='relative w-full overflow-hidden rounded-xl border border-white/10 bg-gray-900/50 shadow-inner'>
          {customContent ? (
            customContent
          ) : videoSrc ? (
            <div className='relative'>
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video
                ref={videoRef}
                className={clsxm('w-full object-cover', imageClassName)}
                loop
                muted={!withSound}
                playsInline
                poster={imageSrc}
                controls={showControls && hasStarted}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                // Don't preload - source is only added when user clicks play
                preload='none'
              >
                {/* Only add source when user clicks play - prevents iOS Safari from preloading */}
                {videoLoaded && (
                  <source
                    src={videoSrc}
                    type={
                      videoSrc.endsWith('.webm') ? 'video/webm' : 'video/mp4'
                    }
                  />
                )}
                {imageSrc && (
                  <Image
                    src={imageSrc}
                    alt={imageAlt || title}
                    width={800}
                    height={600}
                    className='w-full'
                    loading='lazy'
                    sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
                  />
                )}
              </video>
              {/* Play button overlay */}
              {!hasStarted && (
                <button
                  onClick={togglePlay}
                  className='absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/60 via-black/20 to-transparent transition-all hover:from-black/70 hover:via-black/30'
                  aria-label='Play video'
                >
                  <div className='flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 shadow-2xl shadow-purple-500/50 transition-all hover:scale-110 hover:shadow-purple-500/70 ring-4 ring-white/20'>
                    <PlayIcon className='h-9 w-9 text-white ml-1 drop-shadow-lg' />
                  </div>
                </button>
              )}
            </div>
          ) : (
            imageSrc && (
              <Image
                src={imageSrc}
                alt={imageAlt || title}
                width={800}
                height={600}
                loading='lazy'
                className={clsxm(
                  'w-full object-cover transition-transform duration-500 group-hover:scale-105',
                  imageClassName,
                )}
              />
            )
          )}
        </div>
      </div>
    </div>
  );
};

const ModelShowcase = () => {
  const [shuffledModels, setShuffledModels] = useState<typeof chatModels>([]);

  useEffect(() => {
    const models = chatModels.filter((m) => !m.hidden);
    // Fisher-Yates shuffle
    const shuffled = [...models];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setShuffledModels(shuffled);
  }, []);

  const models =
    shuffledModels.length > 0
      ? shuffledModels
      : chatModels.filter((m) => !m.hidden);

  // split into 2 columns
  const col1 = models.slice(0, Math.ceil(models.length / 2));
  const col2 = models.slice(Math.ceil(models.length / 2));

  return (
    <div className='relative h-[388px] w-full overflow-hidden bg-slate-900'>
      <div className='absolute inset-0 z-10 bg-gradient-to-b from-slate-900 via-transparent to-slate-900' />
      <div className='grid grid-cols-2 gap-4 p-4'>
        <div className='animate-scroll-y space-y-3'>
          {[...col1, ...col1].map((model, i) => (
            <div
              key={`${model.id}-${i}`}
              className='flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-3 backdrop-blur-sm'
            >
              <model.icon className='h-5 w-5 text-white flex-shrink-0' />
              <span className='text-xs font-medium text-slate-200 truncate'>
                {model.title}
              </span>
            </div>
          ))}
        </div>
        <div
          className='animate-scroll-y-reverse space-y-3'
          style={{ animationDirection: 'reverse' }}
        >
          {[...col2, ...col2].map((model, i) => (
            <div
              key={`${model.id}-${i}`}
              className='flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-3 backdrop-blur-sm'
            >
              <model.icon className='h-5 w-5 text-white flex-shrink-0' />
              <span className='text-xs font-medium text-slate-200 truncate'>
                {model.title}
              </span>
            </div>
          ))}
        </div>
      </div>
      <style jsx>{`
        @keyframes scroll-y {
          0% {
            transform: translateY(0);
          }
          100% {
            transform: translateY(-50%);
          }
        }
        .animate-scroll-y {
          animation: scroll-y 60s linear infinite;
        }
        .animate-scroll-y-reverse {
          animation: scroll-y 60s linear infinite reverse;
        }
      `}</style>
    </div>
  );
};

const ImageSlideshow = () => {
  const images = [
    'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/a075110d-46f4-4056-b2f7-89d129edeaf2.png',
    'https://media.easy-peasy.ai/ce89c991-b523-422c-995d-243bf0abeed7/cfc72215-9ea3-4ad1-b91e-e19a37dc55a2.png',
    'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/4dbab63d-c49a-4206-9ffe-21ceae277562_medium.webp',
    'https://media.easy-peasy.ai/a2761228-36d6-45ea-b69f-fbfa50d36c5b/b2766cb9-f21c-4ec0-b89f-dba49ed61b2f_medium.webp',
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className='relative h-[300px] md:h-[400px] w-full overflow-hidden bg-gray-900'>
      {images.map((src, index) => (
        <div
          key={src}
          className={clsxm(
            'absolute inset-0 transition-opacity duration-1000 ease-in-out',
            index === currentIndex ? 'opacity-100' : 'opacity-0',
          )}
        >
          <Image
            src={src}
            alt={`AI Generated Image ${index + 1}`}
            fill
            className='object-cover'
            sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
            // Load first image eagerly, rest lazily
            loading={index === 0 ? 'eager' : 'lazy'}
            // Only prioritize the first image
            priority={index === 0}
          />
        </div>
      ))}
    </div>
  );
};

// Simplified to single static image for better mobile performance
const PhotoStudioAnimation = () => {
  return (
    <div className='relative h-[400px] md:h-[600px] w-full overflow-hidden bg-slate-900'>
      <Image
        src='/images/photostudio2.jpeg'
        alt='AI Photo Studio'
        fill
        className='object-cover'
        sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 800px'
        loading='lazy'
      />
    </div>
  );
};

const TextToSpeechAnimation = () => {
  return (
    <div className='relative h-[270px] w-full overflow-hidden bg-slate-900 flex flex-col items-center justify-center'>
      <div className='absolute inset-0 bg-gradient-to-b from-slate-900 via-purple-900/20 to-slate-900' />

      {/* Floating Elements - hidden on mobile for performance */}
      <div className='hidden md:block absolute top-1/4 left-1/4 w-32 h-32 bg-purple-500/20 rounded-full blur-[40px] animate-pulse' />
      <div className='hidden md:block absolute bottom-1/4 right-1/4 w-32 h-32 bg-indigo-500/20 rounded-full blur-[40px] animate-pulse delay-700' />

      <div className='relative z-10 flex items-center gap-6'>
        {/* Animated Face/Speaker Icon */}
        <div className='relative'>
          <div className='w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-500/30 z-20 relative'>
            <span className='text-5xl inline-block'>🗣️</span>
          </div>
          {/* Ripple Effect */}
          <div className='absolute inset-0 rounded-2xl border border-purple-500/50 animate-ping opacity-20' />
        </div>

        {/* Speaking Rays */}
        <div className='relative flex items-center h-20'>
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className='absolute left-0 w-8 h-full border-r-4 border-purple-500 rounded-r-full animate-speak-ray opacity-0'
              style={{
                width: `${(i + 1) * 12}px`,
                height: `${(i + 1) * 20 + 20}px`,
                top: '50%',
                transform: 'translateY(-50%)',
                animationDelay: `${i * 0.2}s`,
              }}
            />
          ))}
        </div>
      </div>

      <style jsx>{`
        @keyframes speak-ray {
          0% {
            opacity: 0;
            transform: translateY(-50%) scale(0.8);
          }
          50% {
            opacity: 1;
            transform: translateY(-50%) scale(1);
          }
          100% {
            opacity: 0;
            transform: translateY(-50%) scale(1.2);
          }
        }
        .animate-speak-ray {
          animation: speak-ray 1s ease-out infinite;
        }
      `}</style>
    </div>
  );
};

const MusicGenerationAnimation = () => {
  return (
    <div className='relative h-[300px] md:h-[500px] w-full overflow-hidden bg-gray-950 flex flex-col items-center justify-center perspective-1000'>
      {/* Dynamic Background */}
      <div className='absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-purple-900/20 via-gray-900 to-gray-950' />
      <div className='absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)]' />

      <div className='relative z-10 flex flex-col items-center justify-center w-full h-full'>
        {/* Central Visualizer System */}
        <div className='relative w-64 h-64 md:w-80 md:h-80 flex items-center justify-center'>
          {/* Pulse Rings */}
          <div className='absolute inset-0 rounded-full border border-purple-500/10 animate-ping-slow' />
          <div className='absolute inset-10 rounded-full border border-pink-500/10 animate-ping-slow delay-75' />

          {/* Circular Spectrum - reduced from 40 to 20 elements for mobile performance */}
          <div className='absolute inset-0 animate-spin-very-slow'>
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className='absolute top-1/2 left-1/2 w-1.5 bg-gradient-to-t from-purple-600 to-pink-400 rounded-full origin-bottom'
                style={{
                  height: '40px',
                  transform: `rotate(${i * 18}deg) translateY(-100px)`,
                  animation: `equalizer 1.5s ease-in-out infinite`,
                  animationDelay: `${-i * 0.1}s`,
                  boxShadow: '0 0 10px rgba(236, 72, 153, 0.5)',
                }}
              />
            ))}
          </div>

          {/* Central Disc/Core */}
          <div className='relative w-40 h-40 md:w-48 md:h-48 rounded-full bg-gray-900 border-8 border-gray-800 shadow-[0_0_50px_rgba(168,85,247,0.4)] flex items-center justify-center overflow-hidden z-20'>
            {/* Spinning Gradient Mesh */}
            <div className='absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 animate-spin-slow opacity-80' />
            <div className='absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.4),transparent)] mix-blend-overlay' />

            {/* Vinyl Texture */}
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className='absolute rounded-full border border-white/10'
                style={{
                  inset: `${(i + 1) * 15}%`,
                }}
              />
            ))}

            {/* Floating Music Icon */}
            <div className='relative z-10 w-16 h-16 bg-gray-950/80 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/10'>
              <MusicNoteIcon className='w-8 h-8 text-pink-400 drop-shadow-[0_0_10px_rgba(244,114,182,0.8)] animate-bounce-subtle' />
            </div>
          </div>
        </div>

        {/* Floating Notes Particles — deterministic positions so SSR and CSR
            render identical values (was `Math.random()`, which caused hydration
            mismatches under React 19). */}
        {[
          { left: 28, top: 32, duration: 3.2 },
          { left: 72, top: 28, duration: 4.1 },
          { left: 42, top: 66, duration: 3.6 },
          { left: 66, top: 58, duration: 4.6 },
          { left: 34, top: 74, duration: 3.9 },
          { left: 58, top: 40, duration: 4.3 },
        ].map((pos, i) => (
          <div
            key={i}
            className='absolute text-2xl text-pink-400/60 animate-float-note'
            style={{
              left: `${pos.left}%`,
              top: `${pos.top}%`,
              animationDelay: `${i * 0.5}s`,
              animationDuration: `${pos.duration}s`,
            }}
          >
            {i % 2 === 0 ? '♪' : '♫'}
          </div>
        ))}
      </div>

      <style jsx>{`
        @keyframes equalizer {
          0%,
          100% {
            height: 10px;
            opacity: 0.3;
            transform: rotate(var(--rot)) translateY(-100px) scaleY(1);
          }
          50% {
            height: 50px;
            opacity: 1;
            transform: rotate(var(--rot)) translateY(-100px) scaleY(1.5);
          }
        }
        .animate-spin-very-slow {
          animation: spin 20s linear infinite;
        }
        .animate-spin-slow {
          animation: spin 8s linear infinite;
        }
        .animate-ping-slow {
          animation: ping 3s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        .animate-bounce-subtle {
          animation: bounce-subtle 2s ease-in-out infinite;
        }
        @keyframes bounce-subtle {
          0%,
          100% {
            transform: translateY(-5%);
          }
          50% {
            transform: translateY(5%);
          }
        }
        @keyframes float-note {
          0% {
            transform: translateY(0) scale(0.8);
            opacity: 0;
          }
          20% {
            opacity: 0.8;
          }
          80% {
            opacity: 0.8;
          }
          100% {
            transform: translateY(-100px) scale(1.2);
            opacity: 0;
          }
        }
        .animate-float-note {
          animation: float-note 4s ease-out infinite;
        }
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

const TranscriptionAnimation = () => {
  const [lines, setLines] = useState<{ speaker: string; text: string }[]>([]);
  const [currentText, setCurrentText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  const conversation = [
    { speaker: 'Speaker A', text: 'How does the AI transcription work?' },
    { speaker: 'Speaker B', text: 'It converts speech to text instantly.' },
    { speaker: 'Speaker A', text: 'Does it detect different speakers?' },
    {
      speaker: 'Speaker B',
      text: 'Yes, it automatically identifies speaking. You can get summary, notes and ask questions about the transcript.',
    },
  ];

  useEffect(() => {
    let charIndex = 0;
    let lineIndex = 0;
    let timeoutId: NodeJS.Timeout;

    // Reset state
    setLines([]);
    setCurrentText('');
    setCurrentIndex(0);

    const type = () => {
      const currentLine = conversation[lineIndex];
      if (!currentLine) return;

      if (charIndex <= currentLine.text.length) {
        setCurrentText(currentLine.text.slice(0, charIndex));
        charIndex++;
        const speed = Math.random() * 30 + 30;
        timeoutId = setTimeout(type, speed);
      } else {
        timeoutId = setTimeout(() => {
          if (lineIndex < conversation.length - 1) {
            setLines((prev) => [...prev, currentLine]);
            setCurrentText('');
            lineIndex++;
            setCurrentIndex(lineIndex);
            charIndex = 0;
            type();
          } else {
            timeoutId = setTimeout(() => {
              setLines([]);
              setCurrentText('');
              lineIndex = 0;
              setCurrentIndex(0);
              charIndex = 0;
              type();
            }, 3000);
          }
        }, 800);
      }
    };

    type();
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div className='relative h-[270px] w-full overflow-hidden bg-slate-900 flex flex-col items-center justify-center p-6'>
      <div className='absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-900/10 to-slate-900' />

      {/* Dynamic Waveform */}
      {/* <div className='flex items-center justify-center gap-1 h-12 mb-6 w-full max-w-[200px]'>
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className='w-1.5 bg-purple-500 rounded-full animate-wave'
            style={{
              animationDelay: `${i * 0.05}s`,
              height: '30%',
            }}
          />
        ))}
      </div> */}

      {/* Transcribed Text Container */}
      <div className='w-full max-w-[300px] h-[220px] rounded-xl bg-white/5 border border-white/10 p-4 backdrop-blur-sm relative overflow-hidden flex flex-col'>
        <div className='flex-1 space-y-3'>
          {lines.map((line, i) => (
            <div key={i} className='text-xs'>
              <span
                className={`font-bold mr-2 ${
                  line.speaker === 'Speaker A'
                    ? 'text-blue-400'
                    : 'text-purple-400'
                }`}
              >
                {line.speaker}:
              </span>
              <span className='text-slate-300'>{line.text}</span>
            </div>
          ))}
          <div className='text-xs'>
            <span
              className={`font-bold mr-2 ${
                conversation[currentIndex]?.speaker === 'Speaker A'
                  ? 'text-blue-400'
                  : 'text-purple-400'
              }`}
            >
              {conversation[currentIndex]?.speaker}:
            </span>
            <span className='text-slate-200'>{currentText}</span>
            <span className='inline-block w-1.5 h-3 ml-0.5 bg-purple-400 animate-pulse align-middle' />
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes wave {
          0%,
          100% {
            height: 20%;
            opacity: 0.5;
          }
          50% {
            height: 80%;
            opacity: 1;
          }
        }
        .animate-wave {
          animation: wave 1.2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

const AgentsAnimation = () => {
  const [showChat, setShowChat] = useState(false);
  const [messages, setMessages] = useState<
    { role: 'user' | 'bot'; text: string }[]
  >([]);
  const [isTyping, setIsTyping] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages, isTyping]);

  useEffect(() => {
    let active = true;

    const runSequence = async () => {
      while (active) {
        // Reset
        setShowChat(false);
        setMessages([]);
        setIsTyping(false);

        // Wait then open chat
        await new Promise((r) => setTimeout(r, 1000));
        if (!active) break;
        setShowChat(true);

        // Bot greeting
        await new Promise((r) => setTimeout(r, 500));
        if (!active) break;
        setIsTyping(true);
        await new Promise((r) => setTimeout(r, 800));
        if (!active) break;
        setIsTyping(false);
        setMessages([{ role: 'bot', text: 'Hi! How can I help?' }]);

        // User query 1: Training data
        await new Promise((r) => setTimeout(r, 1200));
        if (!active) break;
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Can I train this on my data?' },
        ]);

        // Bot response 1
        await new Promise((r) => setTimeout(r, 500));
        if (!active) break;
        setIsTyping(true);
        await new Promise((r) => setTimeout(r, 1000));
        if (!active) break;
        setIsTyping(false);
        setMessages((prev) => [
          ...prev,
          { role: 'bot', text: 'Yes! Upload PDF, Notion, or URL.' },
        ]);

        // User query 2: Integrations
        await new Promise((r) => setTimeout(r, 1500));
        if (!active) break;
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'What about integrations?' },
        ]);

        // Bot response 2
        await new Promise((r) => setTimeout(r, 500));
        if (!active) break;
        setIsTyping(true);
        await new Promise((r) => setTimeout(r, 1000));
        if (!active) break;
        setIsTyping(false);
        setMessages((prev) => [
          ...prev,
          { role: 'bot', text: 'I connect with Slack, Zapier, and more.' },
        ]);

        // User query 3: Forms/APIs
        await new Promise((r) => setTimeout(r, 1500));
        if (!active) break;
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'Can it show forms or call APIs?' },
        ]);

        // Bot response 3
        await new Promise((r) => setTimeout(r, 500));
        if (!active) break;
        setIsTyping(true);
        await new Promise((r) => setTimeout(r, 1000));
        if (!active) break;
        setIsTyping(false);
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: 'Yes, display custom forms and trigger API calls.',
          },
        ]);

        // User query 4: Python
        await new Promise((r) => setTimeout(r, 1500));
        if (!active) break;
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: 'And run Python code?' },
        ]);

        // Bot response 4
        await new Promise((r) => setTimeout(r, 500));
        if (!active) break;
        setIsTyping(true);
        await new Promise((r) => setTimeout(r, 1000));
        if (!active) break;
        setIsTyping(false);
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: 'Absolutely! I can execute Python scripts securely.',
          },
        ]);

        // Hold then reset
        await new Promise((r) => setTimeout(r, 4000));
      }
    };

    runSequence();

    return () => {
      active = false;
    };
  }, []);

  return (
    <div className='relative h-[270px] w-full overflow-hidden bg-slate-900 flex items-center justify-center p-4'>
      <div className='absolute inset-0 bg-gradient-to-br from-slate-900 via-indigo-900/10 to-slate-900' />

      {/* Background Abstract Website */}
      <div className='absolute inset-0 p-6 opacity-20 pointer-events-none flex flex-col gap-4'>
        <div className='h-4 w-1/3 bg-slate-600 rounded' />
        <div className='h-32 w-full bg-slate-700/50 rounded' />
        <div className='flex gap-4'>
          <div className='h-20 w-2/3 bg-slate-700/50 rounded' />
          <div className='h-20 w-1/3 bg-slate-700/50 rounded' />
        </div>
      </div>

      {/* Chat Widget */}
      <div
        className={clsxm(
          'relative z-10 w-[260px] h-[220px] bg-slate-800 border border-slate-700 rounded-xl shadow-2xl overflow-hidden flex flex-col transition-all duration-500 ease-in-out origin-bottom-right',
          showChat
            ? 'scale-100 opacity-100 translate-y-0'
            : 'scale-50 opacity-0 translate-y-10',
        )}
      >
        {/* Header */}
        <div className='bg-indigo-600 p-3 flex items-center gap-2 flex-shrink-0'>
          <div className='w-2 h-2 rounded-full bg-green-400 animate-pulse' />
          <span className='text-xs font-bold text-white'>Support Agent</span>
        </div>

        {/* Messages */}
        <div
          ref={chatContainerRef}
          className='p-3 flex-1 flex flex-col gap-2 overflow-y-auto bg-slate-800/95 scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-transparent'
        >
          {messages.map((m, i) => (
            <div
              key={i}
              className={clsxm(
                'flex animate-fade-in-up',
                m.role === 'user' ? 'justify-end' : 'justify-start',
              )}
            >
              <div
                className={clsxm(
                  'max-w-[85%] p-2 rounded-lg text-[10px] leading-relaxed',
                  m.role === 'user'
                    ? 'bg-indigo-600 text-white rounded-tr-none'
                    : 'bg-slate-700 text-slate-200 rounded-tl-none',
                )}
              >
                {m.text}
              </div>
            </div>
          ))}
          {isTyping && (
            <div className='flex justify-start animate-fade-in-up'>
              <div className='bg-slate-700 p-2 rounded-lg rounded-tl-none flex gap-1 items-center h-7'>
                <div
                  className='w-1 h-1 bg-slate-400 rounded-full animate-bounce'
                  style={{ animationDelay: '0ms' }}
                />
                <div
                  className='w-1 h-1 bg-slate-400 rounded-full animate-bounce'
                  style={{ animationDelay: '150ms' }}
                />
                <div
                  className='w-1 h-1 bg-slate-400 rounded-full animate-bounce'
                  style={{ animationDelay: '300ms' }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Input Area (Visual only) */}
        <div className='p-2 border-t border-slate-700 bg-slate-800 flex-shrink-0'>
          <div className='h-6 bg-slate-900 rounded-full w-full opacity-50' />
        </div>
      </div>

      {/* Floating Launcher Button (visible when chat is closed) */}
      <div
        className={clsxm(
          'absolute bottom-6 right-6 w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center shadow-lg shadow-indigo-600/30 transition-all duration-300',
          showChat
            ? 'scale-0 opacity-0'
            : 'scale-100 opacity-100 hover:scale-110',
        )}
      >
        <FaRobot className='w-6 h-6 text-white' />
      </div>

      <style jsx>{`
        .animate-fade-in-up {
          animation: fadeInUp 0.3s ease-out forwards;
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

const MainFeatures = () => {
  const { isSpecialOffer } = useSpecialOffer();
  const { t, lang } = useLocale('home');

  return (
    <div id='features' className='relative py-24 lg:py-32 overflow-hidden'>
      {/* Background Gradients - hidden on mobile for performance */}
      <div className='hidden md:block absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none'>
        <div className='absolute top-[10%] left-[-10%] w-[40%] h-[40%] bg-purple-900/20 rounded-full blur-[120px]' />
        <div className='absolute bottom-[10%] right-[-10%] w-[40%] h-[40%] bg-indigo-900/20 rounded-full blur-[120px]' />
      </div>

      <div className='relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        <div className='text-center mb-20'>
          <h2 className='text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-6'>
            {t('One platform, endless possibilities')}
          </h2>
          <p className='text-xl text-slate-400 max-w-3xl mx-auto'>
            {t(
              'Build websites, generate media, run code, research — all with AI agents that take action for you.',
            )}
          </p>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8'>
          {/* AI Workflows - Large Card */}
          <FeatureCard
            className='md:col-span-2'
            title={t('AI Workflows')}
            description={t(
              'Chain AI operations visually. Connect text generation, image creation, video production, and more in one powerful automated workflow.',
            )}
            icon={ViewGridAddIcon}
            href='/ai-workflows'
            isSpecialOffer={isSpecialOffer}
            t={t}
            customContent={
              <div className='relative h-[300px] md:h-[400px]'>
                <Image
                  src='/images/workflows.jpeg'
                  alt='AI Workflows'
                  fill
                  className='object-cover'
                  sizes='(max-width: 768px) 100vw, 800px'
                  loading='lazy'
                />
              </div>
            }
          />
          {/* Video Generator */}
          <FeatureCard
            className='md:col-span-2 lg:col-span-1'
            title={t('AI Video Generator')}
            description={t(
              'Create stunning videos from images or text. Bring your stories to life with AI-generated video content.',
            )}
            icon={VideoCameraIcon}
            videoSrc='https://media.easy-peasy.ai/assets/video-generator.webm'
            href='/ai-video-generator'
            isSpecialOffer={isSpecialOffer}
            t={t}
            imageClassName='h-[400px]'
          />
          {/* AI Talking Videos */}
          <FeatureCard
            className='md:col-span-2'
            title={t('AI Talking Videos')}
            description={t(
              'Transform any portrait into a lifelike talking avatar with perfect lip-sync. Ideal for personalized messages.',
            )}
            icon={UserIcon}
            videoSrc='https://media.easy-peasy.ai/assets/talking-video.webm'
            imageSrc='https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/01b937f5-9803-4c5a-b68c-925776397072_thumb.webp'
            href='/ai-video-generator/talking'
            isSpecialOffer={isSpecialOffer}
            t={t}
            imageClassName='h-[300px] md:h-[400px]'
            withSound
            showControls
          />

          {/* Marky Chat */}
          <FeatureCard
            title={t('Marky AI Agent')}
            description={t(
              'Build websites, browse the web, run code, generate charts, analyze files, create presentations — powered by GPT, Claude, Gemini, and more.',
            )}
            icon={ChatAlt2Icon}
            customContent={<ModelShowcase />}
            href='/marky'
            isSpecialOffer={isSpecialOffer}
            t={t}
            imageClassName='h-[250px]'
          />

          {/* AI Images */}
          <FeatureCard
            title={t('AI Image Generator')}
            description={t(
              'Generate stunning, high-quality art and photorealistic images in seconds. Unlock your creativity with the most advanced AI models available.',
            )}
            icon={FaImage}
            customContent={<ImageSlideshow />}
            href='/ai-image-generator'
            isSpecialOffer={isSpecialOffer}
            t={t}
            imageClassName='h-[300px] md:h-[400px]'
          />
          {/* Interior Design */}
          <FeatureCard
            className='md:col-span-2'
            title={t('Interior Design')}
            description={t(
              'Turn your space into something amazing. Visualize different styles instantly.',
            )}
            icon={GiSofa}
            videoSrc='https://media.easy-peasy.ai/assets/interior-design.webm'
            href='/ai-interior-design-generator'
            isSpecialOffer={isSpecialOffer}
            t={t}
            imageClassName='h-[300px] md:h-[500px]'
          />

          {/* Templates */}
          {/* <FeatureCard
            title={t('200+ AI Templates')}
            description={t(
              'Create amazing content 10X faster. From blog posts to social media, we have a template for everything.',
            )}
            icon={SparklesIcon}
            imageSrc='/images/presets3.jpg'
            href='/templates'
            isSpecialOffer={isSpecialOffer}
            t={t}
            imageClassName='h-[250px]'
          /> */}

          {/* Transcription */}
          <FeatureCard
            title={t('Audio Transcription')}
            description={t(
              'Quickly and accurately transcribe your audio content. Generate summaries and notes instantly.',
            )}
            icon={GiSoundWaves}
            customContent={<TranscriptionAnimation />}
            href='/ai-transcription'
            isSpecialOffer={isSpecialOffer}
            t={t}
            imageClassName='h-[250px]'
          />

          {/* Text to Speech */}
          <FeatureCard
            title={t('Text to Speech')}
            description={t(
              'Generate high-quality spoken audio in any voice, style, and language.',
            )}
            icon={MicrophoneIcon}
            customContent={<TextToSpeechAnimation />}
            href='/text-to-speech'
            isSpecialOffer={isSpecialOffer}
            t={t}
            imageClassName='h-[250px]'
          />

          {/* Chatbots */}
          <FeatureCard
            className='md:col-span-2 lg:col-span-1'
            title={t('Agents')}
            description={t(
              'Build a custom AI agent trained on your data. Embed it on your website for customer support.',
            )}
            icon={FaRobot}
            customContent={<AgentsAnimation />}
            href='/chatbots'
            isSpecialOffer={isSpecialOffer}
            t={t}
            imageClassName='h-[270px]'
          />

          {/* Photo Studio */}
          <FeatureCard
            className='md:col-span-2'
            title={t('AI Photo Studio')}
            description={t(
              'Create professional-quality photos for products or social media without a studio.',
            )}
            icon={CameraIcon}
            customContent={<PhotoStudioAnimation />}
            href='/ai-photo-generator'
            isSpecialOffer={isSpecialOffer}
            t={t}
            imageClassName='h-[300px] md:h-[500px]'
          />

          {/* Music Generator */}
          <FeatureCard
            className='md:col-span-2 lg:col-span-1'
            title={t('AI Music Generator')}
            description={t(
              'Create professional-quality music in any genre with just a text prompt. From hip-hop to classical, our AI generates custom tracks for your projects in seconds.',
            )}
            icon={MusicNoteIcon}
            customContent={<MusicGenerationAnimation />}
            href='/ai-music-generator'
            isSpecialOffer={isSpecialOffer}
            t={t}
            imageClassName='h-[300px] md:h-[500px]'
          />
        </div>
      </div>
    </div>
  );
};

export default MainFeatures;
